import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Card';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import {
  useSystemOverview,
  useAIUsageStats,
  usePublishingStats,
  useFailedPostStats,
  useMediaCleanupStats,
  useFailedPosts,
  useUpcomingPosts,
  usePublishedPosts,
  useMissedPosts,
  useRetryFailedPost,
} from '@/hooks/useAdmin';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ConfirmDialog } from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import { Select } from '@/components/ui/Select';
import { devLog } from '@/utils/logger';
import { ActionButton } from '@/components/admin/ActionButton';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import apiClient from '@/services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
  faTelegram,
} from '@fortawesome/free-brands-svg-icons';

/** Validate URL protocol to prevent javascript: XSS */
const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

/** Format a date in a specific timezone for tooltip display */
const formatInTimezone = (date: string | Date, timezone: string | null): string | null => {
  if (!timezone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(date));
  } catch {
    return null;
  }
};

/** Platform icon and color lookup for table display */
const PLATFORM_FA: Record<string, { icon: IconDefinition; color: string; label: string }> = {
  FACEBOOK: { icon: faFacebook, color: '#1877F2', label: 'Facebook' },
  INSTAGRAM: { icon: faInstagram, color: '#E4405F', label: 'Instagram' },
  TWITTER: { icon: faXTwitter, color: '#000000', label: 'X (Twitter)' },
  LINKEDIN: { icon: faLinkedin, color: '#0A66C2', label: 'LinkedIn' },
  TIKTOK: { icon: faTiktok, color: '#000000', label: 'TikTok' },
  YOUTUBE: { icon: faYoutube, color: '#FF0000', label: 'YouTube' },
  PINTEREST: { icon: faPinterest, color: '#E60023', label: 'Pinterest' },
  THREADS: { icon: faThreads, color: '#000000', label: 'Threads' },
  BLUESKY: { icon: faBluesky, color: '#0085FF', label: 'Bluesky' },
  GOOGLE_MY_BUSINESS: { icon: faGoogle, color: '#4285F4', label: 'Google Business' },
  TELEGRAM: { icon: faTelegram, color: '#26A5E4', label: 'Telegram' },
};

/** Render a platform name with its FontAwesome brand icon */
const PlatformBadge = ({ platform }: { platform: string }) => {
  const info = PLATFORM_FA[platform.toUpperCase()];
  if (!info) return <span>{platform}</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: info.color }}
      >
        <FontAwesomeIcon icon={info.icon} className="text-white text-[10px]" />
      </span>
      <span>{info.label}</span>
    </span>
  );
};

/**
 * Admin System Health Page
 * Dashboard for monitoring platform health and statistics
 */

// Platform options for filters
const platformOptions = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'threads', label: 'Threads' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'google_my_business', label: 'Google Business' },
  { value: 'telegram', label: 'Telegram' },
];

// Time range options for filters
const failedPostsHoursOptions = [
  { value: '24', label: 'Last 24 hours' },
  { value: '48', label: 'Last 48 hours' },
  { value: '168', label: 'Last 7 days' },
  { value: '720', label: 'Last 30 days' },
  { value: '0', label: 'All time' },
];

const upcomingHoursOptions = [
  { value: '24', label: 'Next 24 hours' },
  { value: '48', label: 'Next 48 hours' },
  { value: '168', label: 'Next 7 days' },
];

export const AdminSystem = () => {
  const { t } = useTranslation('common');
  const { formatDateTime } = useTimezone();
  const [failedPostsPage, setFailedPostsPage] = useState(1);
  const [upcomingPostsPage, setUpcomingPostsPage] = useState(1);
  const [upcomingHours, setUpcomingHours] = useState('24');
  const [failedPostsPlatform, setFailedPostsPlatform] = useState('');
  const [failedPostsHours, setFailedPostsHours] = useState('168'); // Default to last 7 days
  const [upcomingPlatform, setUpcomingPlatform] = useState('');
  const [publishedPostsPage, setPublishedPostsPage] = useState(1);
  const [publishedPostsPlatform, setPublishedPostsPlatform] = useState('');
  const [publishedPostsHours, setPublishedPostsHours] = useState('24');
  const [missedPostsPage, setMissedPostsPage] = useState(1);
  const [missedPostsPlatform, setMissedPostsPlatform] = useState('');
  const [missedPostsHours, setMissedPostsHours] = useState('168'); // Default to last 7 days
  const [retryFailedPostDialog, setRetryFailedPostDialog] = useState<{ open: boolean; id: number | null; platform: string }>({ open: false, id: null, platform: '' });

  // System Settings state
  const [affiliateSettings, setAffiliateSettings] = useState<Record<string, string>>({});
  const [platformSettings, setPlatformSettings] = useState<Record<string, string>>({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [editingSettingKey, setEditingSettingKey] = useState<string | null>(null);
  const [editingSettingValue, setEditingSettingValue] = useState('');
  const [savingSetting, setSavingSetting] = useState(false);

  // Fetch all system health data
  const { data: overview, isLoading: overviewLoading, isError: overviewError } = useSystemOverview();
  const { data: aiUsage, isLoading: aiUsageLoading, isError: aiUsageError } = useAIUsageStats();
  const { data: publishingStats, isLoading: publishingLoading, isError: publishingError } = usePublishingStats();
  const { data: failedPostStats, isLoading: failedPostStatsLoading, isError: failedPostStatsError } = useFailedPostStats();
  const { data: mediaStats, isLoading: mediaStatsLoading, isError: mediaStatsError } = useMediaCleanupStats();
  const { data: failedPosts, isLoading: failedPostsLoading, isError: failedPostsError } = useFailedPosts({
    page: failedPostsPage,
    limit: 10,
    platform: failedPostsPlatform || undefined,
    hours: parseInt(failedPostsHours, 10) || undefined,
  });
  const { data: upcomingPosts, isLoading: upcomingPostsLoading, isError: upcomingPostsError } = useUpcomingPosts({
    page: upcomingPostsPage,
    limit: 10,
    hours: parseInt(upcomingHours, 10),
    platform: upcomingPlatform || undefined,
  });
  const { data: publishedPosts, isLoading: publishedPostsLoading, isError: publishedPostsError } = usePublishedPosts({
    page: publishedPostsPage,
    limit: 10,
    platform: publishedPostsPlatform || undefined,
    hours: parseInt(publishedPostsHours, 10) || undefined,
  });
  const { data: missedPosts, isLoading: missedPostsLoading, isError: missedPostsError } = useMissedPosts({
    page: missedPostsPage,
    limit: 10,
    platform: missedPostsPlatform || undefined,
    hours: parseInt(missedPostsHours, 10) || undefined,
  });
  const retryFailedPost = useRetryFailedPost();

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch all settings (no category filter)
        const response = await apiClient.get('/api/admin/settings');
        const affiliateMap: Record<string, string> = {};
        const platformMap: Record<string, string> = {};

        response.data.data?.forEach((s: { key: string; value: string; category?: string }) => {
          if (s.category === 'affiliate') {
            affiliateMap[s.key] = s.value;
          } else if (s.category === 'platform' || s.key.startsWith('linkedin_')) {
            platformMap[s.key] = s.value;
          }
        });

        setAffiliateSettings(affiliateMap);
        setPlatformSettings(platformMap);
      } catch (err) {
        devLog.error('Error fetching settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleRetryFailedPostConfirm = async () => {
    if (!retryFailedPostDialog.id) return;

    try {
      await retryFailedPost.mutateAsync(retryFailedPostDialog.id);
      toast.success('Post re-queued for publishing');
      setRetryFailedPostDialog({ open: false, id: null, platform: '' });
    } catch (err) {
      toast.error('Failed to retry post');
    }
  };

  const handleSaveSetting = async (key: string, description: string, category: 'affiliate' | 'platform' = 'affiliate') => {
    setSavingSetting(true);
    try {
      await apiClient.patch(`/api/admin/settings/${key}`, {
        value: editingSettingValue,
        category,
        description,
      });
      toast.success('Setting saved');

      // Update the correct state based on category
      if (category === 'platform') {
        setPlatformSettings(prev => ({ ...prev, [key]: editingSettingValue }));
      } else {
        setAffiliateSettings(prev => ({ ...prev, [key]: editingSettingValue }));
      }

      setEditingSettingKey(null);
      setEditingSettingValue('');
    } catch (err) {
      toast.error('Failed to save setting');
    } finally {
      setSavingSetting(false);
    }
  };

  return (
    <div className="space-y-6 min-w-0">
      <AdminPageHeader
        title="System Health"
        subtitle="Monitor platform health and statistics"
      />

      {/* System Settings */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">System Settings</h2>
        <Card padding="md">
          {settingsLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Default Affiliate Commission Rate */}
              <div className="border-b border-gray-100 pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">Default Affiliate Commission Rate</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Default commission percentage for new affiliates (e.g., 20 for 20%)
                    </p>
                  </div>
                  {editingSettingKey === 'affiliate_default_commission_rate' ? (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={editingSettingValue}
                          onChange={(e) => setEditingSettingValue(e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400"
                          placeholder="20"
                        />
                        <span className="text-gray-500">%</span>
                      </div>
                      <button
                        onClick={() => handleSaveSetting('affiliate_default_commission_rate', 'Default commission percentage for new affiliates')}
                        disabled={savingSetting}
                        className="px-3 py-1 bg-primary-600 text-primary-600-contrast text-sm rounded hover:bg-primary-700 disabled:opacity-50"
                      >
                        {savingSetting ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingSettingKey(null);
                          setEditingSettingValue('');
                        }}
                        disabled={savingSetting}
                        className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold text-gray-900">
                        {affiliateSettings['affiliate_default_commission_rate'] || '20'}%
                      </span>
                      <button
                        onClick={() => {
                          setEditingSettingKey('affiliate_default_commission_rate');
                          setEditingSettingValue(affiliateSettings['affiliate_default_commission_rate'] || '20');
                        }}
                        className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50"
                      >
                        <i className={`hm hm-${ICONS.EDIT} mr-1`} />
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* LinkedIn API Version */}
              <div className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">LinkedIn API Version</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      LinkedIn API version for publishing (e.g., 202502). Auto-updates when API returns version error.
                    </p>
                  </div>
                  {editingSettingKey === 'linkedin_api_version' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingSettingValue}
                        onChange={(e) => setEditingSettingValue(e.target.value)}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400"
                        placeholder="202502"
                        maxLength={6}
                      />
                      <button
                        onClick={() => handleSaveSetting('linkedin_api_version', 'LinkedIn API version for publishing', 'platform')}
                        disabled={savingSetting}
                        className="px-3 py-1 bg-primary-600 text-primary-600-contrast text-sm rounded hover:bg-primary-700 disabled:opacity-50"
                      >
                        {savingSetting ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingSettingKey(null);
                          setEditingSettingValue('');
                        }}
                        disabled={savingSetting}
                        className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold text-gray-900 font-mono">
                        {platformSettings['linkedin_api_version'] || '202511'}
                      </span>
                      <button
                        onClick={() => {
                          setEditingSettingKey('linkedin_api_version');
                          setEditingSettingValue(platformSettings['linkedin_api_version'] || '202511');
                        }}
                        className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50"
                      >
                        <i className={`hm hm-${ICONS.EDIT} mr-1`} />
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Platform Overview Stats */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Platform Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={overview?.totalUsers}
            isLoading={overviewLoading}
            isError={overviewError}
            color="blue"
          />
          <StatCard
            title="Total Teams"
            value={overview?.totalTeams}
            isLoading={overviewLoading}
            isError={overviewError}
            color="green"
          />
          <StatCard
            title="Total Posts"
            value={overview?.totalPosts}
            isLoading={overviewLoading}
            isError={overviewError}
            color="purple"
          />
          <StatCard
            title="Total Channels"
            value={overview?.totalChannels}
            isLoading={overviewLoading}
            isError={overviewError}
            color="orange"
          />
        </div>
      </div>

      {/* Publishing Health */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Publishing Health</h2>
        <Card padding="none" className="overflow-hidden">
          {publishingLoading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : publishingError ? (
            <div className="p-8 text-center">
              <p className="text-gray-700">Unable to load publishing statistics</p>
              <p className="text-sm text-gray-600 mt-1">You may not have access to this data</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Platform</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-700">Total Posts</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-700">Successes</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-700">Failures</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-700">Success Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(() => {
                    const statsArray = Array.isArray(publishingStats) ? publishingStats : [];
                    return statsArray.length > 0 ? (
                      statsArray.map((stat) => (
                        <tr key={stat.platform} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900"><PlatformBadge platform={stat.platform} /></td>
                          <td className="px-4 py-3 text-right text-gray-700">{stat.totalPosts}</td>
                          <td className="px-4 py-3 text-right text-green-600 font-medium">{stat.successCount}</td>
                          <td className="px-4 py-3 text-right text-red-600 font-medium">{stat.failureCount}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-semibold ${stat.successRate >= 90 ? 'text-green-600' : stat.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {stat.successRate.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-600">
                          No publishing data available
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Recently Published Posts */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">Recently Published</h2>
          <div className="flex gap-2">
            <Select
              value={publishedPostsPlatform}
              onValueChange={(value) => {
                setPublishedPostsPlatform(value);
                setPublishedPostsPage(1);
              }}
              options={platformOptions}
              placeholder="All Platforms"
              clearable
              clearLabel="All Platforms"
              size="sm"
              className="w-auto min-w-[180px]"
            />
            <Select
              value={publishedPostsHours}
              onValueChange={(value) => {
                setPublishedPostsHours(value);
                setPublishedPostsPage(1);
              }}
              options={failedPostsHoursOptions}
              size="sm"
              className="w-auto min-w-[160px]"
            />
          </div>
        </div>
        <Card padding="none" className="overflow-hidden">
          {publishedPostsLoading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : publishedPostsError ? (
            <div className="p-8 text-center">
              <p className="text-gray-700">Unable to load published posts</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Team</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">User</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Platform</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Channel</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Post</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Published</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const postsData = Array.isArray(publishedPosts?.data) ? publishedPosts.data : [];
                      return postsData.length > 0 ? (
                        postsData.map((post) => (
                          <tr key={post.id} className="hover:bg-gray-50">
                            <td className="px-3 py-3 text-sm text-gray-700 max-w-[130px] truncate">{post.teamName}</td>
                            <Tooltip content={post.userEmail || ''} disabled={!post.userEmail}>
                              <td className="px-3 py-3 text-sm text-gray-600 max-w-[120px] truncate">
                                {post.userName || post.userEmail || '-'}
                              </td>
                            </Tooltip>
                            <td className="px-3 py-3 font-medium text-gray-900"><PlatformBadge platform={post.platform} /></td>
                            <td className="px-3 py-3 text-sm text-gray-600 max-w-[110px] truncate">{post.channelUsername || '-'}</td>
                            <Tooltip content={post.postTitle || post.postCaption || ''} disabled={!post.postTitle && !post.postCaption}>
                              <td className="px-3 py-3 text-sm text-gray-700 max-w-xs truncate">
                                {post.postTitle || post.postCaption?.slice(0, 50) || t('untitled_post')}
                              </td>
                            </Tooltip>
                            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                              <Tooltip
                                content={post.publishedAt && post.teamTimezone ? `Team time (${post.teamTimezone}): ${formatInTimezone(post.publishedAt, post.teamTimezone)}` : ''}
                                disabled={!post.teamTimezone || !post.publishedAt}
                              >
                                <span>{post.publishedAt ? formatDateTime(post.publishedAt) : '-'}</span>
                              </Tooltip>
                            </td>
                            <td className="px-3 py-3 text-center">
                              {post.externalUrl && isSafeUrl(post.externalUrl) ? (
                                <a
                                  href={post.externalUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-700"
                                  title="Open on platform"
                                >
                                  <i className={`hm hm-${ICONS.EXTERNAL_LINK}`} />
                                </a>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-600">
                            No published posts in the selected time range
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {publishedPosts?.meta?.totalPages && publishedPosts.meta.totalPages > 1 ? (
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {publishedPosts.meta.page} of {publishedPosts.meta.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={publishedPosts.meta.page === 1}
                      onClick={() => setPublishedPostsPage(publishedPosts.meta.page - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={publishedPosts.meta.page === publishedPosts.meta.totalPages}
                      onClick={() => setPublishedPostsPage(publishedPosts.meta.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </Card>
      </div>

      {/* AI Usage Stats */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">AI Usage</h2>
        <Card padding="md">
          {aiUsageLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : aiUsageError ? (
            <div className="text-center py-4">
              <p className="text-gray-700">Unable to load AI usage statistics</p>
              <p className="text-sm text-gray-600 mt-1">You may not have access to this data</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">Total Requests</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {aiUsage?.totalRequests?.toLocaleString() || '0'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Tokens</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {aiUsage?.totalTokens?.toLocaleString() || '0'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Cost</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${aiUsage?.totalCost?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
              {(() => {
                const modelStats = aiUsage?.byModel ? Object.entries(aiUsage.byModel) : [];
                return modelStats.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700 mb-2">By Model</p>
                    {modelStats.map(([model, stats]) => (
                      <div key={model} className="flex justify-between items-center py-2 border-t border-gray-100">
                        <span className="text-sm text-gray-700">{model}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">
                            {stats.tokens?.toLocaleString() || '0'} tokens
                          </span>
                          <span className="text-xs text-gray-600 ml-2">
                            (${stats.cost?.toFixed(2) || '0.00'})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
              {(() => {
                const jobTypeStats = aiUsage?.byJobType ? Object.entries(aiUsage.byJobType) : [];
                return jobTypeStats.length > 0 ? (
                  <div className="space-y-2 mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">By Job Type</p>
                    {jobTypeStats.map(([jobType, stats]) => (
                      <div key={jobType} className="flex justify-between items-center py-2 border-t border-gray-100">
                        <span className="text-sm text-gray-700">{jobType}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">
                            {stats.tokens?.toLocaleString() || '0'} tokens
                          </span>
                          <span className="text-xs text-gray-600 ml-2">
                            ({stats.requests?.toLocaleString() || '0'} requests)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
            </>
          )}
        </Card>
      </div>

      {/* Failed Publishing Posts */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">Failed Publishing Posts</h2>
          <div className="flex gap-2">
            <Select
              value={failedPostsPlatform}
              onValueChange={(value) => {
                setFailedPostsPlatform(value);
                setFailedPostsPage(1);
              }}
              options={platformOptions}
              placeholder="All Platforms"
              clearable
              clearLabel="All Platforms"
              size="sm"
              className="w-auto min-w-[180px]"
            />
            <Select
              value={failedPostsHours}
              onValueChange={(value) => {
                setFailedPostsHours(value);
                setFailedPostsPage(1);
              }}
              options={failedPostsHoursOptions}
              size="sm"
              className="w-auto min-w-[160px]"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <StatCard
            title="Total Failed"
            value={failedPostStats?.totalFailed}
            isLoading={failedPostStatsLoading}
            isError={failedPostStatsError}
            color="red"
          />
          <StatCard
            title="Failed Today"
            value={failedPostStats?.failedToday}
            isLoading={failedPostStatsLoading}
            isError={failedPostStatsError}
            color="orange"
          />
          <Card padding="sm">
            {failedPostStatsLoading ? (
              <LoadingSpinner size="sm" />
            ) : failedPostStatsError ? (
              <p className="text-sm text-gray-600">Unable to load</p>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-2">By Platform</p>
                {failedPostStats?.platformCounts && Object.keys(failedPostStats.platformCounts).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(failedPostStats.platformCounts).map(([platform, count]) => (
                      <div key={platform} className="flex justify-between text-sm">
                        <PlatformBadge platform={platform} />
                        <span className="font-semibold text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No failed posts</p>
                )}
              </>
            )}
          </Card>
        </div>

        <Card padding="none" className="overflow-hidden">
          {failedPostsLoading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : failedPostsError ? (
            <div className="p-8 text-center">
              <p className="text-gray-700">Unable to load failed posts</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Team</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">User</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Platform</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Post</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Error</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Scheduled</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Retries</th>
                      <th className="text-right px-3 py-2 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const postsData = Array.isArray(failedPosts?.data) ? failedPosts.data : [];
                      return postsData.length > 0 ? (
                        postsData.map((post) => (
                          <tr key={post.id} className="hover:bg-gray-50">
                            <td className="px-3 py-3 text-sm text-gray-700 max-w-[130px] truncate">{post.teamName}</td>
                            <Tooltip content={post.userEmail || ''} disabled={!post.userEmail}>
                              <td className="px-3 py-3 text-sm text-gray-600 max-w-[120px] truncate">
                                {post.userName || post.userEmail || '-'}
                              </td>
                            </Tooltip>
                            <td className="px-3 py-3 font-medium text-gray-900"><PlatformBadge platform={post.platform} /></td>
                            <Tooltip content={post.postTitle || post.postCaption || ''} disabled={!post.postTitle && !post.postCaption}>
                              <td className="px-3 py-3 text-sm text-gray-700 max-w-xs truncate">
                                {post.postTitle || post.postCaption?.slice(0, 50) || t('untitled_post')}
                              </td>
                            </Tooltip>
                            <Tooltip content={post.lastError || ''} disabled={!post.lastError}>
                              <td className="px-3 py-3 text-sm text-red-600 max-w-xs truncate">
                                {post.lastError || 'Unknown error'}
                              </td>
                            </Tooltip>
                            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                              <Tooltip
                                content={post.scheduledAt && post.teamTimezone ? `Team time (${post.teamTimezone}): ${formatInTimezone(post.scheduledAt, post.teamTimezone)}` : ''}
                                disabled={!post.teamTimezone || !post.scheduledAt}
                              >
                                <span>{formatDateTime(post.scheduledAt)}</span>
                              </Tooltip>
                            </td>
                            <td className="px-3 py-3 text-center text-sm text-gray-700">{post.retryCount}</td>
                            <td className="px-3 py-3 text-right">
                              <ActionButton
                                icon={ICONS.REFRESH}
                                tooltip="Retry Post"
                                onClick={() => setRetryFailedPostDialog({ open: true, id: post.id, platform: post.platform })}
                                disabled={retryFailedPost.isPending}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-gray-600">
                            No failed posts
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {failedPosts?.meta?.totalPages && failedPosts.meta.totalPages > 1 ? (
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {failedPosts.meta.page} of {failedPosts.meta.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={failedPosts.meta.page === 1}
                      onClick={() => setFailedPostsPage(failedPosts.meta.page - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={failedPosts.meta.page === failedPosts.meta.totalPages}
                      onClick={() => setFailedPostsPage(failedPosts.meta.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </Card>
      </div>

      {/* Upcoming Schedule */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">Upcoming Schedule</h2>
          <div className="flex gap-2">
            <Select
              value={upcomingPlatform}
              onValueChange={(value) => {
                setUpcomingPlatform(value);
                setUpcomingPostsPage(1);
              }}
              options={platformOptions}
              placeholder="All Platforms"
              clearable
              clearLabel="All Platforms"
              size="sm"
              className="w-auto min-w-[180px]"
            />
            <Select
              value={upcomingHours}
              onValueChange={(value) => {
                setUpcomingHours(value);
                setUpcomingPostsPage(1);
              }}
              options={upcomingHoursOptions}
              size="sm"
              className="w-auto min-w-[160px]"
            />
          </div>
        </div>
        <Card padding="none" className="overflow-hidden">
          {upcomingPostsLoading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : upcomingPostsError ? (
            <div className="p-8 text-center">
              <p className="text-gray-700">Unable to load upcoming posts</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Team</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">User</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Platform</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Channel</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Post</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Scheduled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const postsData = Array.isArray(upcomingPosts?.data) ? upcomingPosts.data : [];
                      return postsData.length > 0 ? (
                        postsData.map((post) => (
                          <tr key={post.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-700">{post.teamName}</td>
                            <Tooltip content={post.userEmail || ''} disabled={!post.userEmail}>
                              <td className="px-4 py-3 text-sm text-gray-600 max-w-[120px] truncate">
                                {post.userName || post.userEmail || '-'}
                              </td>
                            </Tooltip>
                            <td className="px-4 py-3 font-medium text-gray-900"><PlatformBadge platform={post.platform} /></td>
                            <td className="px-4 py-3 text-sm text-gray-600">{post.channelUsername || '-'}</td>
                            <Tooltip content={post.postTitle || post.postCaption || ''} disabled={!post.postTitle && !post.postCaption}>
                              <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                                {post.postTitle || post.postCaption?.slice(0, 50) || t('untitled_post')}
                              </td>
                            </Tooltip>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <Tooltip
                                content={post.scheduledAt && post.teamTimezone ? `Team time (${post.teamTimezone}): ${formatInTimezone(post.scheduledAt, post.teamTimezone)}` : ''}
                                disabled={!post.teamTimezone || !post.scheduledAt}
                              >
                                <span>{formatDateTime(post.scheduledAt)}</span>
                              </Tooltip>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-600">
                            No upcoming posts in the selected time range
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {upcomingPosts?.meta?.totalPages && upcomingPosts.meta.totalPages > 1 ? (
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {upcomingPosts.meta.page} of {upcomingPosts.meta.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={upcomingPosts.meta.page === 1}
                      onClick={() => setUpcomingPostsPage(upcomingPosts.meta.page - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={upcomingPosts.meta.page === upcomingPosts.meta.totalPages}
                      onClick={() => setUpcomingPostsPage(upcomingPosts.meta.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </Card>
      </div>

      {/* Missed Scheduled Posts */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-800">Missed Scheduled Posts</h2>
            {missedPosts?.meta?.total && missedPosts.meta.total > 0 ? (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                {missedPosts.meta.total}
              </span>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Select
              value={missedPostsPlatform}
              onValueChange={(value) => {
                setMissedPostsPlatform(value);
                setMissedPostsPage(1);
              }}
              options={platformOptions}
              placeholder="All Platforms"
              clearable
              clearLabel="All Platforms"
              size="sm"
              className="w-auto min-w-[180px]"
            />
            <Select
              value={missedPostsHours}
              onValueChange={(value) => {
                setMissedPostsHours(value);
                setMissedPostsPage(1);
              }}
              options={failedPostsHoursOptions}
              size="sm"
              className="w-auto min-w-[160px]"
            />
          </div>
        </div>
        <Card padding="none" className="overflow-hidden">
          {missedPostsLoading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : missedPostsError ? (
            <div className="p-8 text-center">
              <p className="text-gray-700">Unable to load missed posts</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Team</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">User</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Platform</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Channel</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Post</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Was Scheduled</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Missed By</th>
                      <th className="text-right px-3 py-2 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      const postsData = Array.isArray(missedPosts?.data) ? missedPosts.data : [];
                      return postsData.length > 0 ? (
                        postsData.map((post) => (
                          <tr key={post.id} className="hover:bg-gray-50">
                            <td className="px-3 py-3 text-sm text-gray-700 max-w-[130px] truncate">{post.teamName}</td>
                            <Tooltip content={post.userEmail || ''} disabled={!post.userEmail}>
                              <td className="px-3 py-3 text-sm text-gray-600 max-w-[120px] truncate">
                                {post.userName || post.userEmail || '-'}
                              </td>
                            </Tooltip>
                            <td className="px-3 py-3 font-medium text-gray-900"><PlatformBadge platform={post.platform} /></td>
                            <td className="px-3 py-3 text-sm text-gray-600 max-w-[110px] truncate">{post.channelUsername || '-'}</td>
                            <Tooltip content={post.postTitle || post.postCaption || ''} disabled={!post.postTitle && !post.postCaption}>
                              <td className="px-3 py-3 text-sm text-gray-700 max-w-xs truncate">
                                {post.postTitle || post.postCaption?.slice(0, 50) || t('untitled_post')}
                              </td>
                            </Tooltip>
                            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                              <Tooltip
                                content={post.scheduledAt && post.teamTimezone ? `Team time (${post.teamTimezone}): ${formatInTimezone(post.scheduledAt, post.teamTimezone)}` : ''}
                                disabled={!post.teamTimezone || !post.scheduledAt}
                              >
                                <span>{post.scheduledAt ? formatDateTime(post.scheduledAt) : '-'}</span>
                              </Tooltip>
                            </td>
                            <td className="px-3 py-3 text-sm font-medium text-orange-600 whitespace-nowrap">{post.missedBy}</td>
                            <td className="px-3 py-3 text-right">
                              <ActionButton
                                icon={ICONS.REFRESH}
                                tooltip="Retry Post"
                                onClick={() => setRetryFailedPostDialog({ open: true, id: post.id, platform: post.platform })}
                                disabled={retryFailedPost.isPending}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-gray-600">
                            No missed posts found
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {missedPosts?.meta?.totalPages && missedPosts.meta.totalPages > 1 ? (
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {missedPosts.meta.page} of {missedPosts.meta.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={missedPosts.meta.page === 1}
                      onClick={() => setMissedPostsPage(missedPosts.meta.page - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={missedPosts.meta.page === missedPosts.meta.totalPages}
                      onClick={() => setMissedPostsPage(missedPosts.meta.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </Card>
      </div>

      {/* Storage & Media */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Storage & Media</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            title="Total Storage"
            value={mediaStats?.totalStorageMB}
            suffix=" MB"
            isLoading={mediaStatsLoading}
            isError={mediaStatsError}
            color="indigo"
          />
          <StatCard
            title="Orphaned Files"
            value={mediaStats?.orphanedFilesCount}
            isLoading={mediaStatsLoading}
            isError={mediaStatsError}
            color="yellow"
          />
        </div>
      </div>

      {/* Retry Failed Post Confirmation Dialog */}
      <ConfirmDialog
        open={retryFailedPostDialog.open}
        onOpenChange={(open) => !open && setRetryFailedPostDialog({ open: false, id: null, platform: '' })}
        title="Retry Failed Post"
        description={`Retry publishing this ${retryFailedPostDialog.platform} post? This will re-queue the post for publishing.`}
        confirmText="Retry"
        variant="default"
        onConfirm={handleRetryFailedPostConfirm}
        loading={retryFailedPost.isPending}
      />
    </div>
  );
};

/**
 * Stat Card Component
 * Reusable card for displaying single statistics
 */
interface StatCardProps {
  title: string;
  value?: number;
  suffix?: string;
  isLoading: boolean;
  isError?: boolean;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'yellow' | 'indigo';
}

const StatCard = ({ title, value, suffix = '', isLoading, isError = false, color }: StatCardProps) => {
  const colorClasses: Record<StatCardProps['color'], string> = {
    blue: 'bg-primary-50 text-primary-800',
    green: 'bg-green-50 text-green-800',
    purple: 'bg-peach text-secondary-700',
    orange: 'bg-orange-50 text-orange-800',
    red: 'bg-red-50 text-red-800',
    yellow: 'bg-yellow-50 text-yellow-800',
    indigo: 'bg-soft-mint text-secondary-700',
  };

  return (
    <div className={`rounded-lg border border-gray-200 p-4 ${colorClasses[color]}`}>
      <p className="text-sm opacity-90 mb-1 font-medium">{title}</p>
      {isLoading ? (
        <LoadingSpinner size="sm" />
      ) : isError ? (
        <p className="text-sm text-gray-600">Unable to load</p>
      ) : (
        <p className="text-3xl font-bold">
          {value !== undefined ? value.toLocaleString() : 0}{suffix}
        </p>
      )}
    </div>
  );
};

export default AdminSystem;
